export const LOGICAL_WIDTH = 360;

// Logical viewport height is computed from the device's aspect ratio at
// module load so the Phaser canvas (Scale.FIT) covers the screen instead of
// letterboxing taller phones. Width stays fixed at 360 — only vertical room
// scales — so playfield layout (walls, holes, bar bounds) is unchanged.
// Layout that needs to remain proportional to the visible viewport uses
// ratios (e.g. LOGICAL_HEIGHT * 0.34); absolute layout uses LOGICAL_WIDTH.
//
// Clamped to a minimum of 640 (the historical portrait reference) so that
// landscape browsers (w > h, e.g. desktop) don't end up with a stubby
// playfield. In that case Scale.FIT letterboxes the portrait canvas inside
// the wide viewport, which is the desired desktop behavior.
const BASE_LOGICAL_HEIGHT = 640;
function computeLogicalHeight(): number {
  if (typeof window === 'undefined') return BASE_LOGICAL_HEIGHT;
  // jsdom (Vitest's unit-test env) reports a desktop-ish 1024x768 viewport.
  // The pure-logic unit tests assume the historical 640 value, so we keep
  // it for non-browser runtimes.
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) {
    return BASE_LOGICAL_HEIGHT;
  }
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (!w || !h) return BASE_LOGICAL_HEIGHT;
  return Math.max(BASE_LOGICAL_HEIGHT, Math.round(LOGICAL_WIDTH * (h / w)));
}

export const LOGICAL_HEIGHT = computeLogicalHeight();
export const WORLD_HEIGHT = 160840;  // scrollable world height — fits 500-level climb

// Supersampling factor. The canvas backing-store is RENDER_SCALE× the logical
// viewport, and each scene's camera is zoomed by RENDER_SCALE so world coords
// still map 1:1 to logical units (physics, sizes, layout unchanged).
// Canvas-backed textures (lava field, hole RT) also draw at this multiplier
// and downscale on display so they stay crisp under camera zoom.
//
// Capped at 2× even on DPR-3 mobile WebViews: the post-FX chain (lava-field
// shader + StarfieldHolesPostFX + TrippyPostFX + per-object glow filters) is
// fragment-bound, and stepping from 2 to 3 raises the per-pass fragment count
// 2.25× — enough to thermal-throttle the SoC within a minute on sustained
// play. The fuzziness from upscaling 720×1280 → ~1080×1920 device pixels is
// the accepted trade for steady 60fps.
export const RENDER_SCALE = 2;
